import React from 'react';
import styled from 'styled-components';
import { AppleLogo, GooglePlayLogo } from '@phosphor-icons/react';
import { MotionReveal } from '@components/motion/scroll-motion';
import { FooterCta, FooterCtaRow } from '@pages/landing-page.styles';
import { colors } from '@config/colors';
import { media } from '@config/breakpoints';
import { spacing } from '@config/spacing';
import { typography } from '@config/typography';
import { storeCtaProps } from './store-cta';

/*
 * Where to get it. One action needs one calm panel: the wish, one line of
 * what it's for, and both stores side by side in the hero's own coral
 * buttons (June, 2026-09-23). The Demos row ends on the same words.
 */
export const WISH_TODAY = {
  heading: 'Wish Today',
  /* The Result's promise, in June's words (2026-09-23). */
  sub: 'Your efforts for a joyful, thriving family — amplified.',
} as const;

export const DownloadPanel: React.FC = () => (
  <MotionReveal>
    <Panel>
      <Eyebrow>Download</Eyebrow>
      <Heading>{WISH_TODAY.heading}</Heading>
      <Sub>{WISH_TODAY.sub}</Sub>
      <FooterCtaRow>
        <FooterCta as="a" {...storeCtaProps('ios', 'download_section')}>
          <AppleLogo weight="fill" /> App Store
        </FooterCta>
        <FooterCta as="a" {...storeCtaProps('android', 'download_section')}>
          <GooglePlayLogo weight="fill" /> Google Play
        </FooterCta>
      </FooterCtaRow>
    </Panel>
  </MotionReveal>
);

const Panel = styled.div`
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  padding: ${spacing['3xl']} ${spacing.xl};
  border-radius: 40px;
  text-align: center;
  /* A lighter glass than the cards': the page's water shows through, just
     a bit (June, 2026-09-23). */
  background: ${colors.overlayWhite50};
  border: 1px solid ${colors.overlayWhite80};
  box-shadow: 0 30px 80px rgba(30, 74, 66, 0.1);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);

  ${media.md} {
    padding: ${spacing['5xl']} ${spacing['4xl']};
  }
`;

const Eyebrow = styled.div`
  font-family: ${typography.fontFamily.small};
  font-size: ${typography.fontSize.xs};
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${colors.brand.exxxtraDark};
  margin-bottom: ${spacing.lg};
`;

const Heading = styled.h2`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: clamp(2rem, 4.6vw, 3.25rem);
  font-weight: 300;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: ${colors.neutral.extraDark};
  margin: 0 0 ${spacing.md};
`;

const Sub = styled.p`
  max-width: 26em;
  margin: 0 auto ${spacing['2xl']};
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: clamp(1rem, 1.6vw, 1.25rem);
  font-weight: 300;
  line-height: 1.5;
  color: ${colors.highlight.primary};
`;
